import { api } from '../lib/api'
import { useToast } from '../lib/toast'
import { formatWeekRange, isSameWeek, relativeWeekLabel } from '../lib/dates'
import type { WeekSummary } from '@shared/types'

interface Props {
  weeks: WeekSummary[]
  onOpen: (startDate: string) => void
  onChanged: () => void
  onError: (err: unknown, fallback: string) => void
}

export function HistoryPage({ weeks, onOpen, onChanged, onError }: Props) {
  const toast = useToast()

  async function remove(week: WeekSummary) {
    if (!confirm(`Delete the week of ${formatWeekRange(week.startDate)}? This cannot be undone.`)) {
      return
    }
    try {
      await api.deleteWeek(week.id)
      toast.notify('Week deleted.')
      onChanged()
    } catch (err) {
      onError(err, 'Could not delete that week.')
    }
  }

  if (weeks.length === 0) {
    return (
      <div className="empty">
        <span className="big" aria-hidden="true">
          📚
        </span>
        No weeks planned yet.
      </div>
    )
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>History</h1>
          <p>Every week you&rsquo;ve planned. Open one to reuse it.</p>
        </div>
      </div>

      <div className="stack" style={{ gap: 8 }}>
        {weeks.map((week) => (
          <div className="row" key={week.id}>
            <button
              className={`week-row ${isSameWeek(week.startDate) ? 'current' : ''}`}
              onClick={() => onOpen(week.startDate)}
            >
              <span className="body">
                <span className="title">{formatWeekRange(week.startDate)}</span>
                <span className="sub">
                  {relativeWeekLabel(week.startDate)} · {week.packedDays} day
                  {week.packedDays === 1 ? '' : 's'} packed · {week.itemCount} item
                  {week.itemCount === 1 ? '' : 's'}
                  {week.offDays > 0 && ` · ${week.offDays} off`}
                  {week.noteCount > 0 &&
                    ` · ${week.noteCount} note${week.noteCount === 1 ? '' : 's'}`}
                  {week.updatedBy && ` · last touched by ${week.updatedBy}`}
                </span>
              </span>
            </button>
            <button
              className="btn ghost icon danger"
              onClick={() => remove(week)}
              aria-label={`Delete week of ${formatWeekRange(week.startDate)}`}
              title="Delete week"
            >
              🗑
            </button>
          </div>
        ))}
      </div>
    </>
  )
}
